import { Box, TextField } from '@mui/material';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import { TemplateForm } from './TemplateForm';
import FileDisplay from '../FileDisplay';
import FileDragAndDropField from '../FileDragAndDropField';
import { TEMPLATES_UPLOAD_COUNT_LIMIT, TEMPLATES_UPLOAD_SIZE_LIMIT } from '../../../../types/constants/template';

type Props = {
  loading: boolean;
  control: Control<TemplateForm>;
  register: UseFormRegister<TemplateForm>;
  setValue: UseFormSetValue<TemplateForm>;
  setError: UseFormSetError<TemplateForm>;
  clearErrors: UseFormClearErrors<TemplateForm>;
  errors: FieldErrors<TemplateForm>;
  filesValue: File[];
};

const TemplateImport = ({ loading, control, register, setValue, setError, clearErrors, errors, filesValue }: Props) => (
  <>
    <TextField
      {...register('name')}
      variant="outlined"
      sx={{ mt: '1rem' }}
      disabled={loading}
      error={Boolean(errors.name)}
      helperText={errors.name?.message}
      label="Name"
      margin="dense"
      size="small"
      fullWidth
    />
    <Controller
      control={control}
      name="files"
      render={() => (
        <FileDragAndDropField
          multiple
          error={errors.files?.message}
          accept={{
            'application/acad': ['.dwt'],
          }}
          fileSizeLimit={TEMPLATES_UPLOAD_SIZE_LIMIT}
          onDropRejected={(rejectedFiles) => {
            const errorMessage =
              rejectedFiles[0].errors[0].code === 'file-too-large'
                ? `File size exceeds ${TEMPLATES_UPLOAD_SIZE_LIMIT / 1024 / 1024}MB`
                : 'Invalid file type. Only .dwt files are allowed.';

            setError('files', {
              type: 'custom',
              message: errorMessage,
            });
          }}
          onDrop={(files: (File & { handle?: FileSystemHandle })[]) => {
            if ((filesValue ?? []).length === TEMPLATES_UPLOAD_COUNT_LIMIT) {
              setError('files', {
                type: 'custom',
                message: `You can only upload ${TEMPLATES_UPLOAD_COUNT_LIMIT} file(s).`,
              });
              return;
            }

            // Fix class-transformer issue with FileSystemHandle
            files.forEach((file) => {
              const newFile = file;

              delete newFile?.handle;
            });

            const limit = TEMPLATES_UPLOAD_COUNT_LIMIT - (filesValue ?? []).length;

            setValue('files', [...files.slice(0, limit), ...(filesValue ?? [])]);

            clearErrors('files');
          }}
        />
      )}
    />
    {(filesValue ?? []).length > 0 && (
      <Box sx={{ mt: 1 }}>
        <FileDisplay
          maxFiles={TEMPLATES_UPLOAD_COUNT_LIMIT}
          files={Array.from(filesValue ?? []).sort((a, b) => a.name.localeCompare(b.name))}
          onRemove={(file: File) => {
            const updatedFiles = Array.from(filesValue ?? []).filter((f) => f.name !== file.name);
            setValue('files', updatedFiles);
          }}
        />
      </Box>
    )}
  </>
);

export default TemplateImport;
